<template>
  <v-container>
    <v-row justify="center">
      <v-col cols="12">
        <v-card class="pa-4" elevation="4">
          <v-card-title>
            {{ isEditing ? 'Editar Anúncio' : 'Novo Anúncio' }}
          </v-card-title>

          <v-card-text>
            <v-tabs v-model="currentTab" dark class="mb-6">
              <v-tab value="details">Detalhes do Anúncio</v-tab>
              <v-tab value="photos">Fotos</v-tab>
            </v-tabs>

            <v-form ref="formRef" @submit.prevent="handleSubmit">
              <v-window v-model="currentTab" class="mt-2">
                <!-- DETALHES -->
                <v-window-item value="details">
                  <v-row class="mt-2">
                    <v-col cols="12" sm="2" v-if="isEditing">
                      <v-text-field v-model="formValue.code" label="Código" disabled></v-text-field>
                    </v-col>

                    <v-col cols="12" sm="6">
                      <v-text-field v-model="formValue.title" label="Título do Anúncio" :rules="[rules.title]" />
                    </v-col>

                    <v-col cols="12" sm="4">
                      <v-select v-model="formValue.category_id" :items="categoryOptions" item-title="label"
                        item-value="value" label="Categoria" :rules="[rules.category]" />
                    </v-col>

                    <v-col cols="12" sm="3">
                      <v-text-field v-model="amountFormatted" label="Valor (R$)" :rules="[rules.amount]"
                        @input="formatAmount" />
                    </v-col>

                    <v-col cols="12" sm="3">
                      <v-text-field v-model="formValue.discount" type="number" label="Desconto (R$)" />
                    </v-col>

                    <v-col cols="12" sm="3">
                      <v-text-field v-model="formValue.amount_featured" type="number"
                        label="Valor em Destaque (R$)" />
                    </v-col>

                    <v-col cols="12" sm="3">
                      <v-text-field v-model="formValue.total_amount" type="number" label="Valor Total (R$)" />
                    </v-col>

                    <v-col cols="12">
                      <v-textarea v-model="formValue.description" label="Descrição Completa"
                        :rules="[rules.description]" />
                    </v-col>
                  </v-row>

                  <v-divider class="my-4" />

                  <h4 class="text-h6 mb-4">Localização</h4>
                  <v-row>
                    <v-col cols="12" sm="3">
                      <v-text-field v-model="formValue.zip_code" v-maska="'#####-###'" label="CEP"
                        @blur="consultaCEP" :loading="isCepLoading" />
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-text-field v-model="formValue.address" label="Endereço" />
                    </v-col>
                    <v-col cols="12" sm="3">
                      <v-text-field v-model="formValue.neighborhood" label="Bairro" />
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-text-field v-model="formValue.city" label="Cidade" :rules="[rules.city]" />
                    </v-col>
                    <v-col cols="12" sm="2">
                      <v-text-field v-model="formValue.state" label="UF" />
                    </v-col>
                  </v-row>

                  <v-divider class="my-4" />

                  <h4 class="text-h6 mb-4">Publicação e Cliente</h4>
                  <v-row>
                    <v-col cols="12" sm="6">
                      <v-select v-model="formValue.client_id" :items="clientOptions" item-title="label"
                        item-value="value" label="Cliente / Anunciante" />
                    </v-col>

                    <v-col cols="12" sm="6">
                      <v-select v-model="formValue.coupon_id" :items="couponOptions" item-title="label"
                        item-value="value" label="Cupom de Desconto" clearable />
                    </v-col>

                    <v-col cols="12" sm="4">
                      <v-select v-model="formValue.status" :items="statusOptions" item-title="label"
                        item-value="value" label="Situação" />
                    </v-col>

                    <v-col cols="12" sm="4">
                      <v-switch v-model="formValue.is_active" color="success" label="Ativo?" />
                    </v-col>

                    <v-col cols="12" sm="4">
                      <v-switch v-model="formValue.is_featured" color="warning" label="Em Destaque?" />
                    </v-col>

                    <v-col cols="12" sm="4">
                      <v-text-field v-model="formValue.starts_at" type="datetime-local" label="Início da Publicação" />
                    </v-col>

                    <v-col cols="12" sm="4">
                      <v-text-field v-model="formValue.expires_at" type="datetime-local" label="Expiração" />
                    </v-col>
                  </v-row>
                </v-window-item>

                <!-- FOTOS -->
                <v-window-item value="photos">
                  <v-row>
                    <v-col cols="12">
                      <h4 class="text-h6 mb-4">Foto Principal</h4>
                      <v-file-input v-model="mainImage" label="Selecionar foto principal" accept="image/*"
                        prepend-icon="mdi-camera" show-size />
                      <div v-if="mainImagePreview" class="relative mt-2">
                        <v-img :src="mainImagePreview" height="200px" contain class="rounded-lg" />
                        <v-icon icon="mdi-close-circle" color="red" size="28"
                          class="absolute top-2 right-2 cursor-pointer bg-white rounded-full shadow-md"
                          @click="removeMainImage" />
                      </div>
                    </v-col>

                    <v-col cols="12">
                      <h4 class="text-h6 mb-4">Outras Fotos</h4>
                      <v-file-input v-model="otherImages" label="Selecionar outras fotos" multiple chips show-size
                        accept="image/*" :rules="[rules.maxImageSize]" />
                      <div v-if="allOtherImagePreviews.length" class="d-flex flex-wrap gap-2 mt-4">
                        <div v-for="(img, i) in allOtherImagePreviews" :key="i" class="relative">
                          <v-img :src="img.url" height="100" width="150" contain class="rounded-lg" />
                          <v-icon icon="mdi-close-circle" color="red" size="24"
                            class="absolute top-1 right-1 cursor-pointer bg-white rounded-full"
                            @click="removeOtherImage(i)" />
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </v-window-item>
              </v-window>
            </v-form>
          </v-card-text>

          <v-card-actions class="d-flex justify-space-between">
            <v-btn color="secondary" variant="flat" @click="handleCancel">
              <v-icon left>mdi-arrow-left</v-icon>
              Voltar
            </v-btn>
            <v-btn color="primary" variant="flat" :loading="isSubmitting" :disabled="isSubmitting"
              @click="handleSubmit">
              <v-icon left>mdi-content-save</v-icon>
              {{ isEditing ? 'Salvar Alterações' : 'Cadastrar Anúncio' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { vMaska } from "maska/vue";
import api from '@/services/api';
import { useToastStore } from '@/store/toast';
import { useLoadingStore } from '@/store/loadingStore';

const toastStore = useToastStore();
const loadingStore = useLoadingStore();
const route = useRoute();
const router = useRouter();
const formRef = ref(null);

const userData = ref(null);
const isLoading = ref(false);
const isSubmitting = ref(false);
const isCepLoading = ref(false);
const app_url_api_storage = process.env.VUE_APP_URL_API_STORAGE || '';

const currentTab = ref('details');

const mainImage = ref(null);
const otherImages = ref([]);
const existingMainImageUrl = ref('');
const existingOtherImages = ref([]);
const imagesToDelete = ref([]);
const categoryOptions = ref([]);
const couponOptions = ref([]);
const clientOptions = ref([]);

const isEditing = computed(() => !!route.params.id);

const formValue = ref({
  // core
  title: '',
  subtitle: '',
  code: '',
  description: '',
  category_id: null,
  coupon_id: null,
  tags: '',
  // finance - updated names
  amount: null, // valor base
  discount: null,
  amount_featured: null,
  total_amount: null,
  address: '',
  neighborhood: '',
  city: '',
  state: '',
  zip_code: '',
  client_id: null,
  user_id: null,
  enterprise_id: null,
  status: 'in_analysis',
  is_active: true,
  is_featured: false,
  terms_accepted: false,
  starts_at: null,
  expires_at: null,
});

const amountFormatted = ref('');
const isCepLoadingRef = ref(false);

const statusOptions = [
  { label: 'Em Análise', value: 'in_analysis' },
  { label: 'Aprovado', value: 'approved' },
  { label: 'Rejeitado', value: 'rejected' },
];

const rules = {
  title: v => !!v || 'O título é obrigatório.',
  category: v => !!v || 'A categoria é obrigatória.',
  description: v => !!v || 'A descrição é obrigatória.',
  status: v => !!v || 'A situação é obrigatória.',
  amount: () => (formValue.value.amount !== null && formValue.value.amount !== undefined && formValue.value.amount >= 0) || 'O valor é obrigatório.',
  city: v => !!v || 'A cidade é obrigatória.',
  maxImageSize: v => !v || v.every(file => file.size < 15 * 1024 * 1024) || 'Cada imagem deve ter no máximo 15MB.',
};

function toLocalDatetimeInput(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  const pad = (n) => (String(n).length === 1 ? '0' + n : n);
  const yyyy = d.getFullYear();
  const MM = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mm = pad(d.getMinutes());
  return `${yyyy}-${MM}-${dd}T${hh}:${mm}`;
}
function fromLocalDatetimeInput(localStr) {
  if (!localStr) return null;
  const d = new Date(localStr);
  return d.toISOString();
}

const loadCategories = async () => {
  try {
    const response = await api.get('/categories', { params: { is_active: 1 } });
    categoryOptions.value = response.data.data.map(cat => ({
      label: cat.description,
      value: cat.id,
    }));
  } catch (error) {
    console.error('Erro ao carregar categorias:', error);
    toastStore.showToast({ message: error?.response?.data?.error || 'Erro ao carregar categorias.', color: 'error' });
  }
};

const loadCoupons = async () => {
  try {
    const response = await api.get('/coupons', { params: { is_active: 1 } });
    couponOptions.value = response.data.data.map(coupon => ({
      label: `${coupon.code} (${coupon.value} ${coupon.type === 'percent' ? '%' : 'R$'})`,
      value: coupon.id,
    }));
  } catch (error) {
    console.error('Erro ao carregar cupons:', error);
    toastStore.showToast({ message: error?.response?.data?.error || 'Erro ao carregar cupons.', color: 'error' });
  }
};

const loadClients = async () => {
  try {
    const response = await api.get('/clients');
    clientOptions.value = response.data.data.map(client => ({
      label: client.name,
      value: client.id,
    }));
  } catch (error) {
    console.error('Erro ao carregar a lista de clientes:', error);
    toastStore.showToast({ message: error?.response?.data?.error || 'Erro ao carregar a lista de clientes.', color: 'error' });
  }
};

const loadAdvertisement = async (id) => {
  isLoading.value = true;
  loadingStore.show();
  try {
    const response = await api.get(`/advertisements/${id}`);
    const data = response.data.data;

    if (!data) {
      toastStore.showToast({ message: 'O anúncio não foi encontrado.', color: 'warning' });
      return;
    }

    formValue.value = {
      ...formValue.value,
      ...data,
      amount: data.amount !== null && data.amount !== undefined ? parseFloat(data.amount) : null,
      discount: data.discount !== null && data.discount !== undefined ? parseFloat(data.discount) : null,
      amount_featured: data.amount_featured !== null && data.amount_featured !== undefined ? parseFloat(data.amount_featured) : null,
      total_amount: data.total_amount !== null && data.total_amount !== undefined ? parseFloat(data.total_amount) : null,
      is_active: data.is_active === 1 || data.is_active === true,
      is_featured: data.is_featured === 1 || data.is_featured === true,
      terms_accepted: data.terms_accepted === 1 || data.terms_accepted === true,
      category_id: data.category_id || null,
      coupon_id: data.coupon_id || null,
      client_id: data.client_id || null,
      enterprise_id: data.enterprise_id || userData.value?.enterprise_id || null,
      starts_at: data.starts_at ? toLocalDatetimeInput(data.starts_at) : null,
      expires_at: data.expires_at ? toLocalDatetimeInput(data.expires_at) : null,
    };

    if (data.images && (typeof data.images === 'string' ? data.images.length > 0 : (Array.isArray(data.images) && data.images.length > 0))) {
      const main = data.image;
      let others = [];
      try {
        others = typeof data.images === 'string' ? JSON.parse(data.images) : (Array.isArray(data.images) ? data.images : []);
      } catch {
        others = [];
      }

      existingMainImageUrl.value = main ? `${app_url_api_storage}/images/${main}` : '';
      existingOtherImages.value = others.map(img => `${app_url_api_storage}/images/${img}`);
    } else {
      existingMainImageUrl.value = '';
      existingOtherImages.value = [];
    }

    amountFormatted.value = formValue.value.amount !== null
      ? new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(formValue.value.amount)
      : '';

    await nextTick();
    if (formRef.value) formRef.value.resetValidation();
  } catch (error) {
    console.error('Erro ao carregar os dados do anúncio:', error);
    toastStore.showToast({ message: error?.response?.data?.error || 'Erro ao carregar os dados do anúncio.', color: 'error' });
  } finally {
    isLoading.value = false;
    loadingStore.hide();
  }
};

const mainImagePreview = computed(() => {
  if (mainImage.value) return URL.createObjectURL(mainImage.value);
  return existingMainImageUrl.value || '';
});

const allOtherImagePreviews = computed(() => {
  const existing = existingOtherImages.value.map(url => ({ url, isExisting: true }));
  const added = otherImages.value.map(file => ({ url: URL.createObjectURL(file), isExisting: false, file }));
  return [...existing, ...added];
});

const removeMainImage = () => {
  if (existingMainImageUrl.value) {
    imagesToDelete.value.push(existingMainImageUrl.value.replace(`${app_url_api_storage}/`, ''));
  }
  existingMainImageUrl.value = '';
  mainImage.value = null;
};

const removeOtherImage = (index) => {
  const img = allOtherImagePreviews.value[index];
  if (!img) return;
  if (img.isExisting) {
    imagesToDelete.value.push(img.url.replace(`${app_url_api_storage}/`, ''));
    existingOtherImages.value.splice(existingOtherImages.value.indexOf(img.url), 1);
  } else {
    const fileIndex = otherImages.value.findIndex(f => f === img.file);
    if (fileIndex !== -1) otherImages.value.splice(fileIndex, 1);
  }
};

const formatAmount = (event) => {
  const raw = event.target ? event.target.value : event;
  const clean = String(raw).replace(/[^\d]/g, '');
  const num = clean ? parseInt(clean, 10) / 100 : 0;
  formValue.value.amount = isNaN(num) ? null : num;
  amountFormatted.value = formValue.value.amount !== null
    ? new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(formValue.value.amount)
    : '';
};

watch([() => formValue.value.amount, () => formValue.value.discount, () => formValue.value.amount_featured], () => {
  const a = Number(formValue.value.amount || 0);
  const d = Number(formValue.value.discount || 0);
  const f = Number(formValue.value.amount_featured || 0);
  const total = Math.max(0, a - d + (formValue.value.is_featured ? f : 0));
  formValue.value.total_amount = Number(total.toFixed(2));
});

watch(() => formValue.value.is_featured, (val) => {
  const a = Number(formValue.value.amount || 0);
  const d = Number(formValue.value.discount || 0);
  const f = Number(formValue.value.amount_featured || 0);
  const total = Math.max(0, a - d + (val ? f : 0));
  formValue.value.total_amount = Number(total.toFixed(2));
});

const handleSubmit = async () => {
  const { valid } = await formRef.value.validate();
  if (!valid) {
    currentTab.value = 'details';
    return;
  }

  isSubmitting.value = true;
  const formData = new FormData();

  const payload = { ...formValue.value };

  if (payload.starts_at) payload.starts_at = fromLocalDatetimeInput(payload.starts_at);
  if (payload.expires_at) payload.expires_at = fromLocalDatetimeInput(payload.expires_at);

  for (const key in payload) {
    const val = payload[key];
    if (key === 'tags' && val) {
      const tagsArray = String(val).split(/[\n,]+/).map(t => t.trim()).filter(t => t.length);
      formData.append(key, JSON.stringify(tagsArray));
    } else if (typeof val === 'boolean') {
      formData.append(key, val ? 1 : 0);
    } else if (val !== null && val !== undefined && val !== '') {
      formData.append(key, val);
    }
  }

  if (mainImage.value) formData.append('mainImage', mainImage.value);
  otherImages.value.forEach((file, idx) => formData.append(`otherImages[${idx}]`, file));

  if (imagesToDelete.value.length) formData.append('imagesToDelete', JSON.stringify(imagesToDelete.value));

  try {
    const config = { headers: { 'Content-Type': 'multipart/form-data' } };

    if (isEditing.value) {
      formData.append('_method', 'PUT');
      await api.post(`/advertisement-admin/${route.params.id}`, formData, config);
    } else {
      await api.post('/advertisements', formData, config);
    }

    router.push('/app/anuncios');
    toastStore.showToast({
      message: isEditing.value ? 'Anúncio atualizado com sucesso!' : 'Anúncio cadastrado com sucesso!',
      color: 'success',
    });
  } catch (error) {
    console.error('Erro ao salvar anúncio:', error);
    toastStore.showToast({ message: error?.response?.data?.error || 'Erro ao salvar anúncio.', color: 'error' });
  } finally {
    isSubmitting.value = false;
  }
};

async function consultaCEP() {
  const cep = formValue.value.zip_code ? String(formValue.value.zip_code).replace(/\D/g, '') : null;
  if (!cep || cep.length !== 8) return;

  isCepLoading.value = true;
  try {
    const response = await api.get('/cep', { params: { cep } });
    if (response.data && !response.data.erro) {
      formValue.value.address = response.data.logradouro;
      formValue.value.neighborhood = response.data.bairro;
      formValue.value.city = response.data.localidade;
      formValue.value.state = response.data.uf;
      toastStore.showToast({ message: 'CEP encontrado com sucesso!', color: 'success' });
    } else {
      toastStore.showToast({ message: 'CEP não encontrado. Preencha manualmente.', color: 'warning' });
    }
  } catch (err) {
    console.error('Erro consulta CEP:', err);
    toastStore.showToast({ message: 'Erro na consulta de CEP.', color: 'error' });
  } finally {
    isCepLoading.value = false;
  }
}

const handleCancel = () => { router.push('/app/anuncios'); };

const BASE_ROUTE_PATH = '/app/clientes/editar/';
function goToClientEdit(clientId) {
  if (clientId) {
    const url = `${BASE_ROUTE_PATH}${clientId}`;
    window.open(url, '_blank');
  }
}

onMounted(() => {
  const stored = localStorage.getItem('userData');
  if (stored) {
    userData.value = JSON.parse(stored);
    if (userData.value && userData.value.enterprise_id) {
      formValue.value.enterprise_id = userData.value.enterprise_id;
    }
  }

  loadClients();
  loadCategories();
  loadCoupons();

  if (isEditing.value) {
    loadAdvertisement(route.params.id);
  }
});

watch(() => formValue.value.amount, (val) => {
  amountFormatted.value = val !== null && val !== undefined
    ? new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(val))
    : '';
}, { immediate: true });

</script>
